import { useState, useRef, type FormEvent, useCallback } from 'react';

interface QuoteModalProps {
  open: boolean;
  onClose: () => void;
}

export default function QuoteModal({ open, onClose }: QuoteModalProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const size = file.size;
      if (size < 1024) {
        setFileSize(`${size} B`);
      } else if (size < 1024 * 1024) {
        setFileSize(`${(size / 1024).toFixed(1)} KB`);
      } else {
        setFileSize(`${(size / (1024 * 1024)).toFixed(1)} MB`);
      }
    } else {
      setFileName('');
      setFileSize('');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file && fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
      setFileName(file.name);
      const size = file.size;
      if (size < 1024) {
        setFileSize(`${size} B`);
      } else if (size < 1024 * 1024) {
        setFileSize(`${(size / 1024).toFixed(1)} KB`);
      } else {
        setFileSize(`${(size / (1024 * 1024)).toFixed(1)} MB`);
      }
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName('');
    setFileSize('');
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      setFileName('');
      setFileSize('');
      return;
    }

    // Remove honeypot from payload
    formData.delete('company_alt');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        setStatus('error');
        setErrorMsg('Backend is not connected. Please check your Supabase configuration.');
        return;
      }

      const functionUrl = `${supabaseUrl}/functions/v1/submit-quote`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: formData,
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (!response.ok || parsed?.code !== 'OK') {
        setStatus('error');
        setErrorMsg(serverMsg || 'Submission failed. Please try again.');
        return;
      }

      setStatus('success');
      form.reset();
      setFileName('');
      setFileSize('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
    }
  };

  const handleClose = () => {
    if (status !== 'submitting') {
      onClose();
      setTimeout(() => {
        setStatus('idle');
        setErrorMsg('');
        setFileName('');
        setFileSize('');
        if (formRef.current) formRef.current.reset();
      }, 300);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background-950/80 backdrop-blur-sm transition-opacity"></div>

      {/* Modal */}
      <div
        className="relative w-full max-w-lg bg-background-900 border border-background-700/60 rounded-xl shadow-2xl overflow-hidden animate-modal-in max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-background-800/50 shrink-0">
          <div>
            <h2
              id="quote-modal-title"
              className="text-lg font-bold text-foreground-50 tracking-wide uppercase"
            >
              {status === 'success' ? 'Quote Submitted' : 'Request a Quote'}
            </h2>
            <p className="text-xs text-foreground-500 mt-0.5">
              {status === 'success'
                ? 'We will get back to you shortly'
                : 'Fill out the form and we will respond within 24 hours'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-200 hover:bg-background-800/60 transition-colors"
            aria-label="Close modal"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-line text-lg"></i>
            </span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-3 overflow-y-auto custom-scrollbar">
          {status === 'success' ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-primary-500/20 flex items-center justify-center">
                <span className="w-7 h-7 flex items-center justify-center text-primary-400">
                  <i className="ri-check-line text-xl"></i>
                </span>
              </div>
              <h3 className="text-base font-semibold text-foreground-50 mb-2">
                Thank you for your request!
              </h3>
              <p className="text-sm text-foreground-400 mb-5 max-w-xs mx-auto">
                Our team will review your quote request and contact you within 24 hours.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 bg-primary-500 text-background-950 text-sm font-bold rounded-md hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Name */}
                <div>
                  <label htmlFor="quote-name" className="block text-xs font-medium text-foreground-400 mb-1 uppercase tracking-wider">
                    Name <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="quote-name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    className="w-full px-3 py-2 bg-background-950 border border-background-700/60 rounded-md text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="quote-email" className="block text-xs font-medium text-foreground-400 mb-1 uppercase tracking-wider">
                    Email <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="quote-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="w-full px-3 py-2 bg-background-950 border border-background-700/60 rounded-md text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Make and Model */}
                <div>
                  <label htmlFor="quote-make-model" className="block text-xs font-medium text-foreground-400 mb-1 uppercase tracking-wider">
                    Make and Model
                  </label>
                  <input
                    id="quote-make-model"
                    name="make_model"
                    type="text"
                    className="w-full px-3 py-2 bg-background-950 border border-background-700/60 rounded-md text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors"
                    placeholder="e.g. Siemens SGT-800"
                  />
                </div>

                {/* Serial or Part Number */}
                <div>
                  <label htmlFor="quote-serial" className="block text-xs font-medium text-foreground-400 mb-1 uppercase tracking-wider">
                    Serial or Part Number
                  </label>
                  <input
                    id="quote-serial"
                    name="serial_part_number"
                    type="text"
                    className="w-full px-3 py-2 bg-background-950 border border-background-700/60 rounded-md text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors"
                    placeholder="e.g. 1MK92001 or PN-48392-A"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="quote-message" className="block text-xs font-medium text-foreground-400 mb-1 uppercase tracking-wider">
                  Message
                </label>
                <textarea
                  id="quote-message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 bg-background-950 border border-background-700/60 rounded-md text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors resize-none"
                  placeholder="Describe what you need, quantity, urgency, and any other details..."
                />
                <p className="text-[10px] text-foreground-600 mt-0.5 text-right">Max 500 characters</p>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-xs font-medium text-foreground-400 mb-1 uppercase tracking-wider">
                  Attachment
                </label>
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                  className="relative border-2 border-dashed border-background-700/60 rounded-md p-3 text-center cursor-pointer hover:border-primary-500/50 hover:bg-background-950/50 transition-colors"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="attachment"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip,.rar"
                  />
                  {fileName ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-400">
                        <i className="ri-file-line"></i>
                      </span>
                      <span className="text-sm text-foreground-200 truncate max-w-[200px]">{fileName}</span>
                      <span className="text-xs text-foreground-500">({fileSize})</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                        className="w-6 h-6 flex items-center justify-center text-foreground-500 hover:text-red-400 transition-colors"
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-close-line"></i>
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      <span className="w-7 h-7 mx-auto flex items-center justify-center text-foreground-500">
                        <i className="ri-upload-cloud-2-line text-lg"></i>
                      </span>
                      <p className="text-sm text-foreground-400">
                        <span className="text-primary-400 font-medium">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-foreground-600">
                        PDF, DOC, XLS, PNG, JPG up to 10MB
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Honeypot */}
              <div className="quote-honeypot">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {/* Error */}
              {status === 'error' && errorMsg && (
                <div className="flex items-start gap-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-md">
                  <span className="w-4 h-4 flex items-center justify-center text-red-400 mt-0.5 shrink-0">
                    <i className="ri-error-warning-line"></i>
                  </span>
                  <p className="text-xs text-red-300">{errorMsg}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-500 text-background-950 text-sm font-bold rounded-md hover:bg-primary-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all whitespace-nowrap"
              >
                {status === 'submitting' ? (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center animate-spin">
                      <i className="ri-loader-4-line"></i>
                    </span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-send-plane-line"></i>
                    </span>
                    Submit Quote Request
                  </>
                )}
              </button>

              <p className="text-[10px] text-foreground-600 text-center">
                By submitting, you agree to our privacy policy and terms of service.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}